'use client';
import { ToggleGroup, ToggleGroupItem, Typography } from '@v2/ui';

import {
  trackGa4ContentSelection,
  trackGa4Event,
  trackGa4ItemView,
} from '@v2/analytics/ga4';
import { buildEventGa4Item } from '@v2/analytics/ecommerce';
import { buildPackageInitValuesV2 } from '@config/buildPackage';
import { TrackedEvents } from '@config/index';
import routes from '@config/routes';
import { useScrollToTopOnMount } from '@hooks/use-scroll-top-on-mount';
import { useBetterMediaQuery } from '@hooks/use-ssr-media-query';
import instance from '@instance';
import { LoggedInUserProps } from '@interfaces/auth';
import invalidatePackageData from '@utils/cache-invalidate';
import { links, v2Links } from '@utils/navigation/links';
import { getCookie } from 'cookies-next';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useTransition } from 'react';
import { v4 as uuidv4 } from 'uuid';

import Progress from '@components/common/progress';
import { ILocationResult } from '@components/searchCity';
import {
  FLIGHT_SEARCH_STORAGE,
  PACKAGE_STORAGE,
  UUID_STORAGE,
} from '@config/storageKeys';
import { useCurrencyContext } from '@contexts/currency-context';
import useStorage from '@hooks/useStorage';
import { clearAdminReplaceParams } from '@hooks/use-admin-replace-params';
import { LeagueEvent } from '@interfaces/APITravel';
import { useBuildPackagesStore } from '@store/build-package-store';
import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { useFlightSearchStore } from '@store/flight-search-store';
import { getInternationalFullDate } from '@utils/dates';
import { addShoppingCart } from '@v2/action/shopping-cart/shopping-cart-action';
import { ShoppingCartStepEnum } from '@v2/action/shopping-cart/types';
import { PageLoader } from '@v2/components';
import { PrefetchHotelSearchArea } from '@v2/views/SearchHotel/components/PrefetchHotelSearchArea';
import { cn } from '@v2/utils/cn';
import { addDays, differenceInDays, subDays } from 'date-fns';
import { CONCERTS_ID } from '../events/util';
import { TrustFeatures } from './components/TrustFeatures';
import { useInitBuildPackage } from './use-init-build-package';

export type PackageType = 'packages' | 'tickets';

interface BuyPackageContentProps {
  event: LeagueEvent;
  user: LoggedInUserProps | null;
  hotelLocation: ILocationResult | undefined;
}

function ArrowRight({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M3.33334 8H12.6667M12.6667 8L8.66667 4M12.6667 8L8.66667 12"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BuyPackageContent({
  user,
  hotelLocation,
  event,
}: BuyPackageContentProps) {
  useScrollToTopOnMount();
  const [selected, setSelected] = React.useState<PackageType | null>(null);
  const [isPending, startTransition] = useTransition();
  const storage = useStorage();
  const searchParams = useSearchParams();
  const eventId = searchParams?.get('eventId');
  const eventLink = searchParams?.get('eventLink');
  const router = useRouter();
  const shoppingCartIdParam = searchParams?.get('cartId');

  const { currency } = useCurrencyContext();
  const { isLoading } = useInitBuildPackage();
  const trackedEventId = useRef<string>();

  useEffect(() => {
    const normalizedEventId = String(event?.id || eventId || '');
    if (!normalizedEventId || trackedEventId.current === normalizedEventId)
      return;

    trackedEventId.current = normalizedEventId;
    trackGa4ItemView({
      item: buildEventGa4Item({
        eventId: normalizedEventId,
        eventName: event.name,
        category: event.category?.slug,
      }),
      params: { event_id: normalizedEventId },
    });
  }, [event, eventId]);

  useEffect(() => {
    const configurationUuid = uuidv4().substring(0, 8);
    storage.setItem(UUID_STORAGE, configurationUuid);
  }, [storage]);

  const initBuildPackage = async (selectedPackageType: PackageType) => {
    // A fresh customer package build — drop any leftover admin item-replacement session
    // so a normal flight/room/ticket selection doesn't get misrouted to the admin
    // confirm page (the session is otherwise only cleared on the confirm page itself).
    clearAdminReplaceParams(storage);

    const values = buildPackageInitValuesV2(
      event,
      hotelLocation,
      selectedPackageType
    );
    const eventDate = event?.occurs_at_local
      ? getInternationalFullDate(event.occurs_at_local, undefined)
      : null;

    const defaultDepartureDate = eventDate
      ? subDays(new Date(eventDate), 1)
      : null;
    const defaultReturnDate = eventDate
      ? addDays(new Date(eventDate), 1)
      : null;
    useFlightSearchStore.setState({
      departureDate: defaultDepartureDate?.toISOString(),
      returnDate: defaultReturnDate?.toISOString(),
    });

    const numOfNights =
      values.hotelStartDate && values.hotelEndDate
        ? differenceInDays(
            new Date(values.hotelEndDate),
            new Date(values.hotelStartDate)
          )
        : 0;

    useBuildPackagesStore.setState({
      ...values,
      isBundle: selectedPackageType === 'packages',
    });

    storage.setItem(
      PACKAGE_STORAGE,
      JSON.stringify({
        ...values,
        isBundle: selectedPackageType === 'packages',
      })
    );

    addShoppingCart({
      packageConfigDetails: {
        ...values,
        numOfNights,
        currency,
        isBundle: selectedPackageType === 'packages',
        eventLink: `${links.buy}?eventId=${eventId}&eventLink=${eventLink}`,
        totalPrice: 0, // Will be calculated later in the flow
        eventDate:
          values.eventDate || event?.occurs_at_local || event?.occurs_at || '',
        eventDateSource:
          values.eventDateSource ||
          (event?.occurs_at_local
            ? 'local'
            : event?.occurs_at
              ? 'utc'
              : 'none'),
        eventName: values.eventName || event?.name || '',
        selected: {
          hotel: selectedPackageType === 'packages',
          tickets: true, // Always include tickets
        },
      } as any,
      currentStep: ShoppingCartStepEnum.BUNDLE_TYPE_SELECTION,
    } as any).then((res) => {
      useCheckoutStepsStore.getState().setCartId(res.id);
      useCheckoutStepsStore.getState().setCartUuid(res.uuid);
    });
  };

  const handleGetNearestDestinationAirport = async () => {
    try {
      const isEventsConcert = CONCERTS_ID.includes(
        Number(event.category.parent.id)
      );
      const cacheKey = `airport_cache_${event.id}_${isEventsConcert ? 'concert' : 'sport'}`;
      const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          const isExpired = Date.now() - timestamp > CACHE_TTL;

          if (!isExpired && data) {
            console.log('Using cached airport data for event:', event.id);
            useFlightSearchStore.setState({
              destinationAirport: data,
            });
            return;
          } else {
            localStorage.removeItem(cacheKey);
          }
        } catch (parseError) {
          console.warn(
            'Invalid cached airport data, will fetch fresh:',
            parseError
          );
          localStorage.removeItem(cacheKey);
        }
      }

      let airportResponse;

      if (isEventsConcert) {
        let cityName = '';

        if (event?.venue?.address?.locality) {
          cityName = event.venue.address.locality;
        } else if (event?.venue?.location) {
          cityName = event.venue.location;
        } else if (hotelLocation?.city_name) {
          cityName = hotelLocation.city_name;
        }

        if (cityName) {
          console.log('Concert event - Searching airports by city:', cityName);
          airportResponse = await instance.server(
            `${routes.airportsByCity}?city=${encodeURIComponent(cityName)}`,
            { method: 'GET' }
          );
        }
      } else {
        if (event?.performances?.length > 0) {
          const primaryPerformer = event.performances.find(
            (p) => p.primary
          )?.performer;
          const teamName =
            primaryPerformer?.name || event.performances[0]?.performer?.name;

          if (teamName) {
            console.log(
              'Sports event - Searching airports by team name:',
              teamName
            );
            airportResponse = await instance.server(
              `${routes.airportsByTeamName}?name=${encodeURIComponent(teamName)}`,
              { method: 'GET' }
            );
          }
        }

        if (!airportResponse) {
          let cityName = '';

          if (event?.venue?.address?.locality) {
            cityName = event.venue.address.locality;
          } else if (event?.venue?.location) {
            cityName = event.venue.location;
          } else if (hotelLocation?.city_name) {
            cityName = hotelLocation.city_name;
          }

          if (cityName) {
            console.log(
              'Sports event fallback - Searching airports by city:',
              cityName
            );
            airportResponse = await instance.server(
              `${routes.airportsByCity}?city=${encodeURIComponent(cityName)}`,
              { method: 'GET' }
            );
          }
        }
      }

      if (airportResponse && airportResponse.ok) {
        const airportData = await airportResponse.json();

        const cachePayload = {
          data: airportData,
          timestamp: Date.now(),
        };
        localStorage.setItem(cacheKey, JSON.stringify(cachePayload));
        console.log('Cached airport data for event:', event.id);

        useFlightSearchStore.setState({
          destinationAirport: airportData,
        });
      } else {
        console.warn('No airport data found or API error');
      }
    } catch (error) {
      console.error('Error fetching nearest destination airport:', error);
    }
  };

  const handleClick = (selectedOption: PackageType) => {
    const packageType =
      selectedOption === 'packages' ? 'package' : 'tickets_only';

    trackGa4Event('package_selected', {
      event_id: eventId,
      package_type: packageType,
    });
    trackGa4ContentSelection({
      contentType: 'package_option',
      contentId: `package_option:${selectedOption}`,
      sectionTitle: 'package_selection',
      surface: 'buy_package',
      params: {
        event_id: eventId,
        package_type: packageType,
      },
    });

    setSelected(selectedOption);
    startTransition(async () => {
      localStorage.removeItem(FLIGHT_SEARCH_STORAGE);
      useFlightSearchStore.getState().clearFlightParams();

      if (selectedOption === 'packages') {
        await handleGetNearestDestinationAirport();
      }

      const anonymousId =
        getCookie('anonymous-user-id')?.toString() ||
        'anonymous-user-id not found';

      instance.server(routes.track, {
        body: JSON.stringify({
          userId: user?.id ? Number(user.id) : undefined,
          anonymousId: user ? undefined : anonymousId,
          trackedEvent: TrackedEvents.BUNDLE_TYPE,
          properties: JSON.stringify({
            isBundle: selectedOption === 'packages',
          }),
        }),
        method: 'POST',
      });

      invalidatePackageData();

      initBuildPackage(selectedOption);

      let url = '';
      if (selectedOption === 'tickets') {
        url = `${v2Links.searchTickets}?packageType=${selectedOption}&eventId=${eventId}&eventLink=${eventLink}`;
      } else {
        url = `${links?.searchFlights}?packageType=${selectedOption}&eventId=${eventId}&eventLink=${eventLink}`;
      }

      router.push(url);
      useCheckoutStepsStore.getState().clearAllState();
    });
  };

  if (shoppingCartIdParam && isLoading) return <PageLoader />;

  return (
    <div className="flex w-full flex-col items-center">
      <PrefetchHotelSearchArea event={event} />
      <Progress loading={isPending} />

      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl md:text-[36px] md:leading-[44px]">
          Choose Your Experience
        </h1>
        <p className="mt-2 text-sm font-medium text-gray-500 sm:text-base">
          One Event. One Trip. Built Your Way.
        </p>
      </div>

      <div className="mt-8 grid w-full grid-cols-1 gap-5 md:mt-10 md:grid-cols-2 md:gap-6">
        <div
          role="button"
          tabIndex={!hotelLocation || isPending ? -1 : 0}
          onClick={() => {
            if (hotelLocation && !isPending) handleClick('packages');
          }}
          onKeyDown={(e) => {
            if (
              (e.key === 'Enter' || e.key === ' ') &&
              hotelLocation &&
              !isPending
            ) {
              e.preventDefault();
              handleClick('packages');
            }
          }}
          onMouseEnter={() => {
            router.prefetch(
              `${links.searchFlights}?packageType=packages&eventId=${eventId}&eventLink=${eventLink}`
            );
          }}
          className={cn(
            'group relative flex flex-col justify-between rounded-2xl bg-white p-6 text-center transition-all duration-200 sm:p-7 md:p-8',
            'border-2 border-primary-500 shadow-sm hover:border-primary-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500/20',
            selected === 'packages' && isPending && 'ring-2 ring-primary-500',
            hotelLocation && 'cursor-pointer',
            !hotelLocation &&
              'cursor-not-allowed border-gray-200 opacity-60 hover:border-gray-200 hover:shadow-sm'
          )}
        >
          {hotelLocation ? (
            <div className="shadow-xs absolute left-3.5 top-3.5 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white sm:left-4 sm:top-4">
              <span className="text-[10px]" aria-hidden="true">
                ★
              </span>
              <span>MOST POPULAR</span>
            </div>
          ) : (
            <div className="absolute left-3.5 top-3.5 inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-500 sm:left-4 sm:top-4">
              <span>HOTEL UNAVAILABLE</span>
            </div>
          )}

          <div className="flex flex-col items-center pt-4 sm:pt-3">
            <div className="flex h-24 items-center justify-center transition-transform duration-300 group-hover:scale-105">
              <Image
                src="/icons/luggage.svg"
                alt="Travel packages"
                width={84}
                height={84}
                priority
              />
            </div>

            <h2 className="mt-4 text-xl font-bold text-gray-900 md:text-2xl">
              Travel Packages
            </h2>
            <p className="mt-2 max-w-[280px] text-sm leading-relaxed text-gray-500">
              Tickets, Hotel and Optional Flights in one customizable package.
            </p>
          </div>

          <div className="mt-7 w-full">
            <button
              type="button"
              disabled={isPending || !hotelLocation}
              onClick={(e) => {
                e.stopPropagation();
                if (hotelLocation && !isPending) handleClick('packages');
              }}
              className={cn(
                'group/btn flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-base font-semibold text-white transition-all duration-150',
                'active:bg-primary-700 bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-4 focus:ring-primary-100',
                (isPending || !hotelLocation) &&
                  'cursor-not-allowed bg-primary-500/70 hover:bg-primary-500/70'
              )}
            >
              <span>Build My Package</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/btn:translate-x-1" />
            </button>
          </div>
        </div>

        <div
          role="button"
          tabIndex={isPending ? -1 : 0}
          onClick={() => {
            if (!isPending) handleClick('tickets');
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !isPending) {
              e.preventDefault();
              handleClick('tickets');
            }
          }}
          onMouseEnter={() => {
            router.prefetch(
              `${v2Links.searchTickets}?packageType=tickets&eventId=${eventId}&eventLink=${eventLink}`
            );
          }}
          className={cn(
            'group relative flex cursor-pointer flex-col justify-between rounded-2xl bg-white p-6 text-center transition-all duration-200 sm:p-7 md:p-8',
            'border border-gray-200 shadow-sm hover:border-gray-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500/20',
            selected === 'tickets' && isPending && 'ring-2 ring-primary-500'
          )}
        >
          <div className="flex flex-col items-center pt-4 sm:pt-3">
            <div className="flex h-24 items-center justify-center transition-transform duration-300 group-hover:scale-105">
              <Image
                src="/icons/plane-tickets.svg"
                alt="Only tickets"
                width={84}
                height={84}
                priority
              />
            </div>

            <h2 className="mt-4 text-xl font-bold text-gray-900 md:text-2xl">
              Only Tickets
            </h2>
            <p className="mt-2 max-w-[280px] text-sm leading-relaxed text-gray-500">
              Verified event tickets without hotel or travel.
            </p>
          </div>

          <div className="mt-7 w-full">
            <button
              type="button"
              disabled={isPending}
              onClick={(e) => {
                e.stopPropagation();
                if (!isPending) handleClick('tickets');
              }}
              className={cn(
                'group/btn flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-base font-semibold transition-all duration-150',
                'border border-primary-500 text-primary-500 hover:border-primary-600 hover:bg-primary-50/70 focus:outline-none focus:ring-4 focus:ring-primary-100',
                isPending && 'cursor-not-allowed opacity-60'
              )}
            >
              <span>Find Tickets Only</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/btn:translate-x-1" />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8 w-full md:mt-10">
        <TrustFeatures />
      </div>
    </div>
  );
}
